import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ArrowUpRight, ArrowDownRight } from "lucide-react";
import { GlassCard } from "./GlassCard";
import { formatAOA } from "@/lib/utils";

const formatValue = (value, format) => {
    if (format === "currency_aoa") return `AOA ${formatAOA(value)}`;
    if (format === "percent") return `${value}%`;
    if (typeof value === "number") return value.toLocaleString("pt-PT");
    return String(value ?? "—");
};

const useCountUp = (target, duration = 1200) => {
    const [value, setValue] = useState(0);
    useEffect(() => {
        if (typeof target !== "number" || isNaN(target)) {
            setValue(target);
            return;
        }
        const start = performance.now();
        const from = 0;
        let raf = 0;
        const tick = (t) => {
            const p = Math.min((t - start) / duration, 1);
            const eased = 1 - Math.pow(1 - p, 3);
            const v = from + (target - from) * eased;
            setValue(target % 1 === 0 ? Math.round(v) : Math.round(v * 10) / 10);
            if (p < 1) raf = requestAnimationFrame(tick);
        };
        raf = requestAnimationFrame(tick);
        return () => cancelAnimationFrame(raf);
    }, [target, duration]);
    return value;
};

export const StatCard = ({ label, value, format = "number", trend, index = 0, testId }) => {
    const animated = useCountUp(typeof value === "number" ? value : 0);
    const displayed = typeof value === "number" ? formatValue(animated, format) : value;
    const trendUp = trend?.direction === "up";
    return (
        <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.08, duration: 0.45, ease: [0.4, 0, 0.2, 1] }}
            data-testid={testId}
        >
            <GlassCard hover padding="p-6" className="relative">
                <p className="font-body text-xs uppercase tracking-[0.14em] text-[color:var(--slate-mid)]">
                    {label}
                </p>
                <div className="mt-3 flex items-end justify-between gap-3">
                    <span className="font-mono text-[2.4rem] leading-[1] text-[color:var(--copper-dark)] font-medium">
                        {displayed}
                    </span>
                    {trend && (
                        <span
                            className={
                                "inline-flex items-center gap-1 text-xs font-mono " +
                                (trendUp ? "text-[color:var(--success)]" : "text-[color:var(--error)]")
                            }
                        >
                            {trendUp ? (
                                <ArrowUpRight size={14} strokeWidth={2} />
                            ) : (
                                <ArrowDownRight size={14} strokeWidth={2} />
                            )}
                            {trend.value}%
                        </span>
                    )}
                </div>
                <div className="mt-5 h-1 w-full rounded-full bg-[color:var(--copper-subtle)] overflow-hidden">
                    <motion.div
                        initial={{ width: 0 }}
                        animate={{ width: `${Math.min(100, 55 + index * 10)}%` }}
                        transition={{ delay: 0.4 + index * 0.08, duration: 0.9 }}
                        className="h-full bg-gradient-to-r from-[color:var(--copper-light)] to-[color:var(--copper)]"
                    />
                </div>
            </GlassCard>
        </motion.div>
    );
};
